/**
 * PRD-66 §FR-4.5 — the roster's notice for students who joined the group
 * after some of its materials were generated.
 *
 * It lives on the page, not in the add-student modal, because professors add
 * students one after another: a step after every add would interrupt each
 * one, while a notice derived from the server's gaps accumulates them and
 * offers ONE action for all. It disappears by itself once nobody is missing,
 * and scrolls itself into view when a NEW late student appears — after an
 * add, the professor is looking at the roster or the add button, not at it.
 *
 * "Revisar y generar" opens the choice inline (no modal: nothing here needs
 * protected focus, and on a phone a second layer is one more thing to
 * dismiss). The trigger stays mounted as the disclosure's toggle; focus moves
 * into the list on open and back to the trigger on cancel. Open materials
 * come pre-selected; materials whose window closed sit behind a second
 * disclosure, unselected — a student who joins mid-semester is not offered
 * every material the group ever had.
 *
 * The outcome is reported in place, not in a toast: success replaces the
 * notice with a confirmation that keeps focus; a material the server refused
 * stays in the list with the reason under it.
 *
 * "Ahora no" is remembered per classroom against the students and materials
 * it hid, and the notice returns only when a NEW one appears (a material
 * closing does not bring it back). Browser storage is a convenience here,
 * never state the server depends on.
 */

import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { cn } from "@lib/cn"
import { formatDate } from "@lib/intl"
import { CheckCircle2, ChevronDown, UserPlus } from "lucide-react"
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useClassroomMaterialGaps, useFillMaterialGaps } from "../hooks"
import { formatLateNames } from "../lateStudents"

import type { MaterialGap, MaterialStatus } from "@/types"

const DISMISS_KEY_PREFIX = "platonica.late_students.dismissed."

/** What "Ahora no" hid: the notice returns only for something not in here. */
interface Dismissal {
  students: string[]
  materials: string[]
}

function readDismissed(classroomId: string): Dismissal | null {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY_PREFIX + classroomId)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Dismissal>
    return Array.isArray(parsed.students) && Array.isArray(parsed.materials)
      ? { students: parsed.students, materials: parsed.materials }
      : null
  } catch {
    // Private mode, blocked storage, or an older value: show the notice.
    return null
  }
}

function writeDismissed(classroomId: string, value: Dismissal) {
  try {
    window.localStorage.setItem(DISMISS_KEY_PREFIX + classroomId, JSON.stringify(value))
  } catch {
    // Same: dismissing is a convenience, not state.
  }
}

/** The house pill for each status a gap can be in (`Badge` owns nowrap). */
const STATUS_BADGE: Partial<Record<MaterialStatus, "accent" | "attention" | "success">> = {
  procesando: "accent",
  por_enviar: "accent",
  revision: "attention",
  enviado: "success",
}

/** Move focus to the first focusable element after `node`, before it unmounts. */
function focusAfter(node: HTMLElement | null) {
  const next = node?.nextElementSibling
  const target = next?.querySelector<HTMLElement>("input, button, a[href], [tabindex='0']")
  window.requestAnimationFrame(() => target?.focus())
}

interface Props {
  classroomId: string
}

export const LateStudentsBanner = memo(function LateStudentsBanner({ classroomId }: Props) {
  const { t } = useTranslation(["materials", "errors"])
  const headingId = useId()
  const panelId = useId()
  const closedId = useId()
  const gaps = useClassroomMaterialGaps(classroomId)
  const fill = useFillMaterialGaps(classroomId)
  const [expanded, setExpanded] = useState(false)
  const [showClosed, setShowClosed] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [refused, setRefused] = useState<Map<string, string>>(new Map())
  const [done, setDone] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState<Dismissal | null>(() => readDismissed(classroomId))
  const sectionRef = useRef<HTMLElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const doneRef = useRef<HTMLDivElement>(null)
  const seenLateRef = useRef<Set<string> | null>(null)

  const data = gaps.data
  const openMaterials = useMemo(() => (data?.materials ?? []).filter((m) => m.is_open), [data])
  const closedMaterials = useMemo(() => (data?.materials ?? []).filter((m) => !m.is_open), [data])
  const nameById = useMemo(() => new Map((data?.students ?? []).map((s) => [s.id, s.name])), [data])
  // Only the open materials decide whether the notice appears and whom it
  // names: closed ones are offered, never announced.
  const lateIds = useMemo(() => {
    const ids = new Set<string>()
    for (const m of openMaterials) for (const id of m.missing_student_ids) ids.add(id)
    return Array.from(ids)
  }, [openMaterials])

  const hiddenByDismissal =
    !!dismissed &&
    lateIds.every((id) => dismissed.students.includes(id)) &&
    openMaterials.every((m) => dismissed.materials.includes(m.material_id))
  const visible = done !== null || (openMaterials.length > 0 && !hiddenByDismissal)

  // A late student who was not here a moment ago means the professor just
  // added them (or imported a list): bring the notice into view, once. The
  // first load sets the baseline and never scrolls.
  useEffect(() => {
    if (!data) return
    const seen = seenLateRef.current
    seenLateRef.current = new Set(lateIds)
    if (seen && visible && lateIds.some((id) => !seen.has(id))) {
      sectionRef.current?.scrollIntoView({ block: "nearest" })
    }
  }, [data, lateIds, visible])

  // Focus follows the disclosure: into the list on open, onto the
  // confirmation once it replaces the notice.
  useEffect(() => {
    if (expanded) panelRef.current?.querySelector<HTMLElement>("input")?.focus()
  }, [expanded])
  useEffect(() => {
    if (done !== null) doneRef.current?.focus()
  }, [done])

  const more = useCallback((count: number) => t("late.more", { count }), [t])
  const namesOf = useCallback(
    (ids: string[]) =>
      formatLateNames(
        ids.map((id) => nameById.get(id) ?? ""),
        ids.length,
        more,
      ),
    [more, nameById],
  )

  if (!visible) return null

  if (done !== null) {
    return (
      <section
        ref={sectionRef}
        aria-labelledby={headingId}
        className="rounded-card border-success bg-success-soft text-success-ink border p-4"
      >
        <div
          ref={doneRef}
          tabIndex={-1}
          role="status"
          className="flex items-start gap-3 focus:outline-none"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <div className="min-w-0 space-y-1">
            <h2 id={headingId} className="text-sm font-semibold">
              {t("late.banner.done", { count: done })}
            </h2>
            <p className="text-sm">{t("late.banner.done_hint")}</p>
          </div>
        </div>
        <Button
          variant="secondary"
          className="mt-3 w-full sm:ml-8 sm:w-auto"
          onClick={() => {
            focusAfter(sectionRef.current)
            setDone(null)
          }}
        >
          {t("late.banner.done_close")}
        </Button>
      </section>
    )
  }

  const openPanel = () => {
    setSelected(new Set(openMaterials.map((m) => m.material_id)))
    setRefused(new Map())
    setShowClosed(false)
    setExpanded(true)
  }
  const closePanel = () => {
    setExpanded(false)
    window.requestAnimationFrame(() => triggerRef.current?.focus())
  }
  const dismiss = () => {
    const value = { students: lateIds, materials: openMaterials.map((m) => m.material_id) }
    writeDismissed(classroomId, value)
    focusAfter(sectionRef.current)
    setDismissed(value)
    setExpanded(false)
  }
  const toggle = (materialId: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(materialId)) next.delete(materialId)
      else next.add(materialId)
      return next
    })

  const all = [...openMaterials, ...closedMaterials]
  const chosen = all.filter((m) => selected.has(m.material_id))
  const copies = chosen.reduce((sum, m) => sum + m.missing_student_ids.length, 0)
  // A sent material emails its late copy on READY: the verb says so, the way
  // the material's own page does ("Generar y enviar").
  const sends = chosen.some((m) => m.status === "enviado")
  const submitLabel =
    copies === 0
      ? t("late.sheet.choose")
      : t(sends ? "late.sheet.submit_send" : "late.sheet.submit", { count: copies })
  const submit = () =>
    fill.mutate(Array.from(selected), {
      onSuccess: (results) => {
        const refusals = new Map(
          results
            .filter((result) => result.error_code)
            .map((result) => [result.material_id, result.error_code as string]),
        )
        if (refusals.size > 0) {
          // Keep the list open: the refused rows stay, each with its reason.
          setRefused(refusals)
          setSelected(new Set(refusals.keys()))
          return
        }
        setExpanded(false)
        setDone(results.reduce((sum, result) => sum + result.created, 0))
      },
    })

  // Two materials with one title cannot be told apart by their row: those
  // rows, and only those, say when each was made.
  const titleCounts = new Map<string, number>()
  for (const m of all) titleCounts.set(m.title, (titleCounts.get(m.title) ?? 0) + 1)
  const duplicateTitles = new Set(
    Array.from(titleCounts.entries())
      .filter(([, n]) => n > 1)
      .map(([title]) => title),
  )

  // A row names its students only when they are not everyone the heading
  // already named: with one late student, "Para Mateo" on every row is noise.
  const rowNames = (m: MaterialGap) =>
    m.missing_student_ids.length === lateIds.length &&
    m.missing_student_ids.every((id) => lateIds.includes(id))
      ? null
      : namesOf(m.missing_student_ids)
  const renderRow = (m: MaterialGap) => (
    <GapRow
      key={m.material_id}
      gap={m}
      showCreated={duplicateTitles.has(m.title)}
      checked={selected.has(m.material_id)}
      onToggle={() => toggle(m.material_id)}
      names={rowNames(m)}
      refusal={
        refused.has(m.material_id)
          ? t(`errors:${refused.get(m.material_id)}`, t("errors:internal_error"))
          : null
      }
    />
  )

  return (
    <section
      ref={sectionRef}
      aria-labelledby={headingId}
      className="rounded-card border-attention bg-attention-soft text-attention-ink border p-4"
    >
      <div className="flex items-start gap-3">
        <UserPlus className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="min-w-0 space-y-1">
          <h2 id={headingId} className="text-sm font-semibold">
            {t("late.banner.title", {
              count: lateIds.length,
              names: namesOf(lateIds),
              materials: t("late.banner.materials", { count: openMaterials.length }),
            })}
          </h2>
          <p className="text-sm">{t("late.banner.reason", { count: lateIds.length })}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:ml-8 sm:flex-row">
        <Button
          ref={triggerRef}
          variant="secondary"
          onClick={expanded ? closePanel : openPanel}
          aria-expanded={expanded}
          aria-controls={panelId}
        >
          {t("late.banner.review")}
        </Button>
        {!expanded ? (
          <Button variant="ghost" onClick={dismiss}>
            {t("late.banner.dismiss")}
          </Button>
        ) : null}
      </div>

      {expanded ? (
        <div id={panelId} ref={panelRef} className="mt-3 sm:ml-8">
          {refused.size > 0 ? (
            <p role="alert" className="text-danger-ink mb-2 text-sm font-medium">
              {t("late.sheet.partial", { count: refused.size })}
            </p>
          ) : null}
          <fieldset>
            <legend className="sr-only">{t("late.sheet.legend")}</legend>
            <ul className="rounded-card border-border bg-surface divide-hairline text-ink divide-y border">
              {openMaterials.map(renderRow)}
            </ul>

            {closedMaterials.length > 0 ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowClosed((v) => !v)}
                  aria-expanded={showClosed}
                  aria-controls={closedId}
                  className="rounded-control focus-visible:ring-accent inline-flex min-h-11 items-center gap-1.5 px-1 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
                >
                  <ChevronDown
                    className={cn("h-4 w-4 transition-transform", showClosed && "rotate-180")}
                    aria-hidden
                  />
                  {showClosed
                    ? t("late.sheet.closed_hide")
                    : t("late.sheet.closed_toggle", { count: closedMaterials.length })}
                </button>
                {showClosed ? (
                  <div id={closedId}>
                    <p className="mb-2 text-sm">{t("late.sheet.closed_hint")}</p>
                    <ul className="rounded-card border-border bg-surface divide-hairline text-ink divide-y border">
                      {closedMaterials.map(renderRow)}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </fieldset>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Button onClick={submit} loading={fill.isPending} disabled={copies === 0}>
              {submitLabel}
            </Button>
            <Button variant="ghost" onClick={closePanel} disabled={fill.isPending}>
              {t("late.sheet.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  )
})

function GapRow({
  gap,
  showCreated,
  checked,
  onToggle,
  names,
  refusal,
}: {
  gap: MaterialGap
  showCreated: boolean
  checked: boolean
  onToggle: () => void
  names: string | null
  refusal: string | null
}) {
  const { t } = useTranslation("materials")
  const consequence =
    gap.status === "por_enviar"
      ? gap.send_date
        ? t("late.sheet.consequence.por_enviar", { date: formatDate(gap.send_date, "PP") })
        : t("late.sheet.consequence.por_enviar_undated")
      : t(`late.sheet.consequence.${gap.status}`)

  return (
    <li>
      {/* The whole row is the label: a 44px target, not a 16px box. */}
      <label className="flex min-h-11 cursor-pointer items-start gap-3 px-3 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="accent-accent mt-0.5 h-5 w-5 shrink-0 md:h-4 md:w-4"
        />
        <span className="min-w-0 flex-1">
          <span className="text-ink mr-2 text-sm font-medium break-words">{gap.title}</span>
          <Badge variant={STATUS_BADGE[gap.status] ?? "neutral"} className="align-middle">
            {t(`status.${gap.status}`)}
          </Badge>
          <span className="text-meta mt-0.5 block">
            {names ? t("late.sheet.row", { names, consequence }) : consequence}
          </span>
          {showCreated ? (
            <span className="text-meta block">
              {t("late.sheet.created_on", { date: formatDate(gap.created_at, "PP") })}
            </span>
          ) : null}
          {refusal ? (
            <span className="text-danger-ink mt-0.5 block text-xs font-medium">{refusal}</span>
          ) : null}
        </span>
      </label>
    </li>
  )
}
